// src/components/AuthContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { auth, onAuthStateChanged, signOut } from '../firebase';
import { GoogleAuthProvider, signInWithPopup, setPersistence, browserSessionPersistence } from "firebase/auth";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [authError, setAuthError] = useState(null); // New state for authorization error

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user);
            setLoading(false);
            // Clear any previous authorization errors on auth state change
            setAuthError(null);
        });

        return unsubscribe;
    }, []);

    const signInWithGoogle = async () => {
        setAuthError(null); // Clear previous errors
        try {
            const provider = new GoogleAuthProvider();
            await setPersistence(auth, browserSessionPersistence);
            await signInWithPopup(auth, provider);
        } catch (error) {
            console.error("Error signing in with Google:", error);
            // This catch block usually handles client-side Firebase auth errors (e.g., popup closed)
            setAuthError("Failed to sign in. Please try again."); // Generic error for popup
        }
    };

    const signOutUser = async () => {
        try {
            await signOut(auth);
        } catch (error) {
            console.error("Error signing out:", error);
            setAuthError("Failed to sign out. Please try again.");
        }
    };

    const value = {
        currentUser,
        signInWithGoogle,
        signOutUser,
        authError, // Provide authError in context
        setAuthError, // Provide setter in context if needed directly
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};